import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Plane, Printer, ArrowLeft } from "lucide-react";
import CommonDateRangePicker from "../../../Common/CommonDateRangePicker/CommonDateRangePicker";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

const HRMSLeaveReport = () => {
  const { state } = useLocation();
  const leavesFromState = state?.leaves || [];
  const rangeFromParent = state?.range || { startDate: "", endDate: "" };

  const [leaves, setLeaves] = useState(leavesFromState);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [range, setRange] = useState(rangeFromParent);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLeaves(leavesFromState || []);
  }, [leavesFromState]);

  useEffect(() => {
    if (!range.startDate || !range.endDate || leaves.length === 0) {
      setFilteredLeaves(leaves);
      return;
    }

    const start = new Date(range.startDate);
    const end = new Date(range.endDate);

    const filtered = leaves.filter((leave) => {
      const from = new Date(leave.start_date);
      const to = new Date(leave.end_date);
      return from <= end && to >= start;
    });

    setFilteredLeaves(filtered);
  }, [range, leaves]);

  const handleDownload = () => {
    if (filteredLeaves.length === 0) {
      Swal.fire("No Data", "No leave records found in this range", "warning");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(filteredLeaves);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leave Report");
    XLSX.writeFile(wb, `Leave_Report_${range.startDate || "All"}_to_${range.endDate || "All"}.xlsx`);
  };

  if (!state) {
    return (
      <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
        <div className="text-center py-5">
          <h5>Please open this report from the <strong>HRMS Reports</strong> page.</h5>
          <Link to="/hrms-reports" className="btn btn-primary mt-3">Go to Reports</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row">
        <div className="col-12">
          <div className="hrms-leave-report-cont">
            <div className="d-flex flex-column flex-lg-row row-gap-3 justify-content-between align-items-center">
              <div className="d-flex gap-3 align-items-center">
                <Plane size={35} stroke="#3b82f6" />
                <div className="d-flex flex-column">
                  <h4 className="fw-semibold m-0">Leave Report</h4>
                  <span className="text-muted">Detailed list of all employees on leave for the selected date range.</span>
                </div>
              </div>
              <Link to="/hrms-reports" className="d-flex align-items-center gap-2 btn hrms-reports-back-btn">
                <ArrowLeft size={20} />
                Back to Reports
              </Link>
            </div>

            <div className="row mt-3 row-gap-2">
              <div className="col-lg-10 col-sm-12 col-12">
                <CommonDateRangePicker label="Date Range" value={range} onChange={setRange} />
              </div>
              <div className="col-lg-2 col-sm-12 col-12 d-flex align-items-end">
                <button className="btn hrms-report-btn w-100" onClick={handleDownload}>
                  <Printer size={18} stroke="#fff" />
                  <span className="text-light">Download Report</span>
                </button>
              </div>
            </div>
          </div>

          <div className="hrms-leave-report-cont mt-4">
            <div className="d-flex flex-column row-gap-1">
              <h4 className="fw-semibold m-0">Leave Records ({filteredLeaves.length})</h4>
              <span className="text-muted">Displaying records from {range.startDate && range.endDate ? `${range.startDate} to ${range.endDate}` : "No range selected yet"}</span>
            </div>

            <div className="table-responsive">
              <table className="table align-middle mb-0 table-hover mt-4">
                <thead>
                  <tr className="text-muted small border-top">
                    <th>Employee Code</th>
                    <th>Employee Name</th>
                    <th>Department</th>
                    <th>Designation</th>
                    <th>Leave Type</th>
                    <th>From Date</th>
                    <th>To Date</th>
                    <th>Duration</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeaves.length > 0 ? (
                    filteredLeaves.map((rec, i) => (
                      <tr key={i}>
                        <td>{rec.employee?.employee_code || rec.employee_code}</td>
                        <td>{rec.employee?.first_name || rec.employee_name || "-"}</td>
                        <td>{rec.employee?.department || rec.department || "-"}</td>
                        <td>{rec.employee?.designation || rec.designation || "-"}</td>
                        <td>{rec.leave_type || "-"}</td>
                        <td>{rec.start_date || "-"}</td>
                        <td>{rec.end_date || "-"}</td>
                        <td>{rec.total_days || "-"}</td>
                        <td>{rec.status || "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center py-3 text-muted">
                        {range.startDate && range.endDate ? "No leave records found for the selected range." : "Please select a date range."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default HRMSLeaveReport;
